import { createTheme } from "@mui/material/styles";
import type { PaletteMode, Theme } from "@mui/material/styles";
// Adds the X date-picker component slots (MuiPickersOutlinedInput, …) to the theme type.
import type {} from "@mui/x-date-pickers/themeAugmentation";
import { tokens } from "@/lib/tokens";

function buildShadows(): Theme["shadows"] {
  const shadows: string[] = ["none"];
  for (let i = 1; i <= 3; i += 1) {
    shadows.push(tokens.shadows.soft);
  }
  for (let i = 4; i <= 24; i += 1) {
    shadows.push(tokens.shadows.float);
  }
  if (shadows.length !== 25) {
    throw new Error(`buildShadows produced ${shadows.length.toString()} entries, expected 25`);
  }
  // Single narrowing cast — MUI's Shadows is a 25-tuple, runtime length is verified above.
  return shadows as Theme["shadows"];
}

export function buildTheme(mode: PaletteMode): Theme {
  const palette = mode === "light" ? tokens.colors : tokens.colorsDark;
  const shadowsArray = buildShadows();

  return createTheme({
    palette: {
      mode,
      primary: { main: palette.primary, contrastText: "#FFFFFF" },
      error: { main: palette.error },
      background: {
        default: palette.background,
        paper: palette.surface,
      },
      text: {
        primary: palette.textMain,
        secondary: palette.textMuted,
      },
      info: { main: palette.infoToast },
      secondary: { main: palette.accent },
    },
    shape: {
      // MUI default base unit. `sx={{ borderRadius: N }}` resolves to N * 4 px.
      // Pill shape for inputs/buttons applied via component overrides below, not globally.
      borderRadius: 4,
    },
    spacing: tokens.spacing.xs,
    breakpoints: {
      values: {
        xs: 0,
        sm: tokens.breakpoints.mobile,
        md: tokens.breakpoints.tablet,
        lg: 1280,
        xl: 1536,
      },
    },
    typography: {
      fontFamily: tokens.typography.bodyFamily,
      fontWeightRegular: tokens.typography.bodyWeightRegular,
      fontWeightBold: tokens.typography.bodyWeightBold,
      h1: {
        fontFamily: tokens.typography.headingFamily,
        fontWeight: 800,
        fontSize: tokens.typography.sizes.displayLarge,
        lineHeight: 1.2,
        letterSpacing: "-0.02em",
      },
      h2: {
        fontFamily: tokens.typography.headingFamily,
        fontWeight: 800,
        fontSize: tokens.typography.sizes.titleLarge,
        lineHeight: 1.25,
        letterSpacing: "-0.015em",
      },
      h3: {
        fontFamily: tokens.typography.headingFamily,
        fontWeight: 700,
        fontSize: tokens.typography.sizes.titleMedium,
        lineHeight: 1.3,
        letterSpacing: "-0.01em",
      },
      h4: {
        fontFamily: tokens.typography.headingFamily,
        fontWeight: 700,
        fontSize: tokens.typography.sizes.bodyLarge,
        lineHeight: 1.35,
        letterSpacing: "-0.005em",
      },
      h5: {
        fontFamily: tokens.typography.headingFamily,
        fontWeight: 600,
        fontSize: tokens.typography.sizes.bodyMedium,
        lineHeight: 1.4,
      },
      h6: {
        fontFamily: tokens.typography.headingFamily,
        fontWeight: 600,
        fontSize: tokens.typography.sizes.labelLarge,
        lineHeight: 1.4,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
      },
      body1: {
        fontFamily: tokens.typography.bodyFamily,
        fontWeight: tokens.typography.bodyWeightRegular,
        fontSize: tokens.typography.sizes.bodyLarge,
        lineHeight: 1.55,
      },
      body2: {
        fontFamily: tokens.typography.bodyFamily,
        fontWeight: tokens.typography.bodyWeightRegular,
        fontSize: tokens.typography.sizes.bodyMedium,
        lineHeight: 1.5,
      },
      caption: {
        fontFamily: tokens.typography.bodyFamily,
        fontWeight: tokens.typography.bodyWeightRegular,
        fontSize: tokens.typography.sizes.labelMedium,
        lineHeight: 1.45,
      },
      button: {
        fontFamily: tokens.typography.bodyFamily,
        fontWeight: tokens.typography.bodyWeightBold,
        fontSize: tokens.typography.sizes.labelLarge,
        textTransform: "none",
        letterSpacing: "0.01em",
      },
    },
    shadows: shadowsArray,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            textRendering: "optimizeLegibility",
            // Kill trackpad horizontal-swipe back/forward navigation app-wide.
            // overscroll-behavior-x:contain only helps on elements that actually
            // overflow; the Kanban row often fits exactly, so the swipe falls
            // through to the page. Setting it on html stops it everywhere.
            overscrollBehaviorX: "none",
          },
          body: {
            fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11", "ss01"',
            overscrollBehaviorX: "none",
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "outlined",
          size: "medium",
          fullWidth: true,
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.input,
            // Soft tinted fill so inputs read as distinct surfaces instead of
            // blending into white cards/dialogs.
            backgroundColor: palette.activeBackground,
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.primary,
              borderWidth: 2,
            },
            // Stronger error outline — 1px is low-salience on the pill shape.
            "&.Mui-error .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.error,
              borderWidth: 2,
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.input,
          },
        },
      },
      // X date-picker uses its own input (not MuiOutlinedInput); mirror the pill
      // radius + tinted fill + focus/error borders so it matches text fields.
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.input,
            backgroundColor: palette.activeBackground,
            "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: palette.primary,
              borderWidth: 2,
            },
            "&.Mui-error .MuiPickersOutlinedInput-notchedOutline": {
              borderColor: palette.error,
              borderWidth: 2,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.card,
            backgroundImage: "none",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.button,
            textTransform: "none",
            fontWeight: tokens.typography.bodyWeightBold,
            minHeight: 46,
          },
          sizeSmall: {
            minHeight: 36,
          },
          sizeLarge: {
            minHeight: 54,
            paddingTop: 12,
            paddingBottom: 12,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.card,
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
          color: "transparent",
        },
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: tokens.radius.card,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.button,
          },
        },
      },
    },
  });
}
